/**
 * PlatformsSection — platform overview or legacy inline tabbed editor.
 *
 * Refactored from the legacy read-only list. Each platform row is collapsible
 * and reveals 4 tabs that map 1:1 to the canonical Platform schema and its
 * children:
 *
 *   • Config     → PlatformConfigEditor (Platform schema fields)
 *   • Voice      → PlatformVoiceEditor  (PlatformVoice schema, voice/voice subdoc)
 *   • Design     → PlatformDesignEditor (PlatformDesignProfile schema, design/design subdoc)
 *   • Posts      → PlatformPostsTable   (author_posts/* — examples are corpus
 *                  rows where exampleForPostTypes.length > 0; toolbar toggle
 *                  switches between "all" and "examples only")
 *   • Templates  → PerAuthorPostTemplatesLibrary (existing)
 *
 * The legacy per-platform "Examples" tab was removed in plan §5 — examples
 * are now multi-tag-flagged corpus posts, not a separate subcollection.
 *
 * In the new Content Plan shell, the `overview` variant is used by the
 * Platforms sub-tab and does not expand platform details inline. The legacy
 * `accordion` variant remains available for tracked-creator dialogs until the
 * route-backed platform workspace is fully adopted everywhere.
 *
 * Header: «+ Add platform» button → AddPlatformDialog.
 *
 * Active state surfaces remain the existing chips (max chars, cadence,
 * algorithm, optimal slots) for at-a-glance scanning.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import RedditIcon from '@mui/icons-material/Reddit';
import LanguageIcon from '@mui/icons-material/Language';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SAccordion, SDialog } from '@modules/ui-components';
import { useAuthorPlatforms, type AuthorPlatformLite } from '../../hooks/useAuthorPlatforms';
import { AddPlatformDialog } from './AddPlatformDialog';
import type { AuthorBasePath } from '../../services/AuthorPlatformService';
import { resolvePlatformOptimalFieldSlots } from '@modules/content-plan/utils/strategyCadence';
import { PlatformWorkspace, type PlatformWorkspaceTabKey } from '@modules/content-plan/components/platforms/PlatformWorkspace';

const TESTID = 'author-platforms-section';
const PLATFORMS_TAB_TESTID = 'content-plan-platforms-tab';
const PLATFORM_OVERVIEW_LIST_TESTID = 'platform-overview-list';

const PLATFORM_ICON: Record<string, React.ReactNode> = {
  linkedin: <LinkedInIcon fontSize="small" />,
  twitter: <TwitterIcon fontSize="small" />,
  instagram: <InstagramIcon fontSize="small" />,
  reddit: <RedditIcon fontSize="small" />,
};

interface Props {
  projectId: string;
  authorId: string | null;
  /** I.1 — userId required to dispatch /author/analyze for re-extract. */
  userId?: string;
  /** Author's defaultLocale — passed to AddPlatformDialog. */
  defaultLocale?: string;
  /**
   * Discriminates between self-author (default `'authors'`) and tracked
   * creator (`'external_authors'`). Forwarded to every child hook /
   * service / component so the entire platforms subtree operates on the
   * correct Firestore collection.
   */
  basePath?: AuthorBasePath;
  /** I.1 — propagate task starts to a parent task tray. */
  onTaskStarted?: (taskId: string, label: string) => void;
  /** `overview` renders the shallow Platforms tab; `accordion` keeps legacy inline tabs. */
  variant?: 'accordion' | 'overview';
}

export const PlatformsSection: React.FC<Props> = ({
  projectId,
  authorId,
  userId,
  defaultLocale,
  basePath = 'authors',
  onTaskStarted,
  variant = 'accordion',
}) => {
  const { platforms, loading } = useAuthorPlatforms(projectId, authorId, basePath);
  const [expandedPlatformId, setExpandedPlatformId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PlatformWorkspaceTabKey>('config');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [workspacePlatformId, setWorkspacePlatformId] = useState<string | null>(null);

  const togglePlatform = (id: string) => {
    setExpandedPlatformId((prev) => (prev === id ? null : id));
    setActiveTab('config'); // reset tab on platform switch
  };

  const header = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent={variant === 'overview' ? 'space-between' : 'flex-end'}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      spacing={1.5}
      sx={{ mb: 2 }}
    >
      {variant === 'overview' && (
        <Box>
          <Typography variant="h6">Platforms</Typography>
          <Typography variant="body2" color="text.secondary">
            {platforms.length === 0
              ? 'No platforms enabled yet'
              : `${platforms.length} ${platforms.length === 1 ? 'platform' : 'platforms'} enabled`}
          </Typography>
        </Box>
      )}
      <Stack direction="row" justifyContent="flex-end">
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          disabled={!authorId}
          data-testid={`${TESTID}-addBtn`}
        >
          Add platform
        </Button>
      </Stack>
    </Stack>
  );

  const body = (
    <>
      {header}
      {loading ? (
        <Typography variant="body2" color="text.secondary">Loading platforms…</Typography>
      ) : platforms.length === 0 ? (
        <Alert severity="info">
          No platforms yet. Onboard an author or click <strong>Add platform</strong>.
        </Alert>
      ) : (
        <Stack spacing={1.5} data-testid={variant === 'overview' ? PLATFORM_OVERVIEW_LIST_TESTID : undefined}>
          {platforms.map((p) => {
            const isExpanded = expandedPlatformId === p.id;
            const isPlatformActive = p.isActive !== false;
            return (
              <Paper
                key={p.id}
                variant="outlined"
                sx={{
                  borderColor: isPlatformActive ? 'divider' : 'action.disabled',
                  opacity: isPlatformActive ? 1 : 0.6,
                  overflow: 'hidden', // ensure inner Tabs border respects rounded corners
                }}
                data-testid={`platform-card-${p.id}`}
              >
                {/* Platform header row — toggles expand/collapse */}
                <PlatformRow
                  platform={p}
                  expanded={isExpanded}
                  variant={variant}
                  onToggle={() => {
                    if (variant === 'overview') {
                      setWorkspacePlatformId(p.id);
                      return;
                    }
                    togglePlatform(p.id);
                  }}
                  onOpenExternal={variant === 'overview' && authorId
                    ? () => {
                      window.open(
                        `/project/${projectId}/content-plan/platform/${encodeURIComponent(p.id)}?author=${encodeURIComponent(authorId)}`,
                        '_blank',
                        'noopener,noreferrer',
                      );
                    }
                    : undefined}
                />

                {/* Expanded body — tabs + tab content. Lives inside the SAME
                    Paper as the header so they share the same border. */}
                {variant === 'accordion' && isExpanded && authorId && (
                  <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ p: 2 }} data-testid={`platform-${p.id}-tabs`}>
                      <PlatformWorkspace
                        projectId={projectId}
                        authorId={authorId}
                        platform={p}
                        userId={userId}
                        basePath={basePath}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        onTaskStarted={onTaskStarted}
                      />
                    </Box>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {authorId && (
        <AddPlatformDialog
          open={addDialogOpen}
          projectId={projectId}
          authorId={authorId}
          userId={userId ?? ''}
          defaultLocale={defaultLocale}
          basePath={basePath}
          onClose={() => setAddDialogOpen(false)}
          onTaskStarted={onTaskStarted}
        />
      )}

      {variant === 'overview' && authorId && (
        <SDialog
          open={Boolean(workspacePlatformId)}
          onClose={() => setWorkspacePlatformId(null)}
          title={`${platforms.find((p) => p.id === workspacePlatformId)?.displayName ?? workspacePlatformId ?? 'Platform'} workspace`}
          fullScreen
          fullWidth
          maxWidth="xl"
          data-testid="content-plan-platform-workspace-dialog"
        >
          {platforms.find((p) => p.id === workspacePlatformId) && (
            <Box sx={{ px: 3, pb: 3, height: 'calc(100vh - 96px)', overflow: 'auto' }}>
              <PlatformWorkspace
                projectId={projectId}
                authorId={authorId}
                platform={platforms.find((p) => p.id === workspacePlatformId)!}
                userId={userId}
                basePath={basePath}
                onTaskStarted={onTaskStarted}
              />
            </Box>
          )}
        </SDialog>
      )}
    </>
  );

  if (variant === 'overview') {
    return (
      <Box data-testid={PLATFORMS_TAB_TESTID} sx={{ p: 2 }}>
        {body}
      </Box>
    );
  }

  return (
    <SAccordion
      title="Platforms"
      subtitle={
        platforms.length === 0
          ? 'No platforms enabled yet'
          : `${platforms.length} ${platforms.length === 1 ? 'platform' : 'platforms'} enabled`
      }
      data-testid={TESTID}
    >
      {body}
    </SAccordion>
  );
};

// ─── Platform row (collapsible header with summary chips) ─────────────

const PlatformRow: React.FC<{
  platform: AuthorPlatformLite;
  expanded: boolean;
  variant: 'accordion' | 'overview';
  onToggle: () => void;
  onOpenExternal?: () => void;
}> = ({ platform, expanded, variant, onToggle, onOpenExternal }) => {
  const icon = PLATFORM_ICON[platform.id] ?? <LanguageIcon fontSize="small" />;
  const isActive = platform.isActive !== false;
  const optimalTimes = resolvePlatformOptimalFieldSlots(platform);
  const cadence = platform.optimalPostFrequency;
  const cadenceLabel = optimalTimes.length > 0
    ? `${optimalTimes.length}/wk`
    : cadence && (cadence.min || cadence.max)
      ? cadence.min === cadence.max
        ? `${cadence.min}/wk`
        : `${cadence.min ?? '?'}-${cadence.max ?? '?'}/wk`
      : null;

  return (
    <Box
      sx={{ p: 2, cursor: 'pointer' }}
      onClick={onToggle}
      data-testid={`platform-row-${platform.id}`}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
          {icon}
        </Box>
        <Typography variant="subtitle2" sx={{ flex: 1, textTransform: 'capitalize' }}>
          {platform.displayName ?? platform.id}
        </Typography>
        <Chip
          label={isActive ? 'active' : 'disabled'}
          size="small"
          color={isActive ? 'success' : 'default'}
          variant={isActive ? 'filled' : 'outlined'}
        />
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (variant === 'overview' && onOpenExternal) {
              onOpenExternal();
              return;
            }
            onToggle();
          }}
          data-testid={`platform-${platform.id}-expand-toggle`}
          title={variant === 'overview' ? 'Open platform in a new window' : expanded ? 'Collapse' : 'Expand'}
        >
          {variant === 'overview'
            ? <OpenInNewIcon fontSize="small" />
            : expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }} useFlexGap>
        {typeof platform.maxPostLength === 'number' && (
          <Chip label={`max ${platform.maxPostLength} chars`} size="small" variant="outlined" />
        )}
        {cadenceLabel && <Chip label={cadenceLabel} size="small" variant="outlined" />}
        {platform.hasAlgorithm && (
          <Chip label="algorithm-driven" size="small" variant="outlined" />
        )}
        {optimalTimes.length > 0 && (
          <Chip label={`${optimalTimes.length} optimal slots`} size="small" variant="outlined" />
        )}
        {platform.profileUrl && (
          <Chip
            label="URL set"
            size="small"
            color="info"
            variant="outlined"
            data-testid={`platform-${platform.id}-url-chip`}
          />
        )}
      </Stack>
    </Box>
  );
};
